import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Calendar, User, Eye, Share2 } from "lucide-react";
import { format } from "date-fns";
import { Navbar } from "@/components/layout/Navbar";
import ReactMarkdown from "react-markdown";
import { BlogPostSEO } from "@/components/seo/BlogPostSEO";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const { data: post, isLoading } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Increment view count
  useEffect(() => {
    if (post) {
      supabase
        .from("blog_posts")
        .update({ view_count: (post.view_count || 0) + 1 })
        .eq("id", post.id)
        .then();
    }
  }, [post]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="responsive-container py-12">
          <div className="max-w-4xl mx-auto">
            <Skeleton className="h-8 w-32 mb-8" />
            <Skeleton className="h-12 w-full mb-4" />
            <Skeleton className="h-6 w-3/4 mb-8" />
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="responsive-container py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl font-bold mb-4">Post Not Found</h1>
            <Button onClick={() => navigate("/blog")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* SEO for Blog Post */}
      <BlogPostSEO
        title={post.title}
        excerpt={post.excerpt || ""}
        content={post.content}
        slug={post.slug}
        category={post.category}
        authorName={post.author_name}
        publishedAt={post.published_at}
        updatedAt={post.updated_at}
        featuredImage={post.featured_image}
        tags={post.tags || []}
      />
      
      <Navbar />
      
      <article className="responsive-container py-12" itemScope itemType="https://schema.org/BlogPosting">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <nav aria-label="Breadcrumb" className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/blog")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
          </nav>

          {/* Header */}
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <Badge variant="secondary" className="text-sm">
                <span itemProp="articleSection">{post.category}</span>
              </Badge>
              <time 
                className="text-sm text-muted-foreground flex items-center gap-1"
                dateTime={post.published_at}
                itemProp="datePublished"
              >
                <Calendar className="h-4 w-4" />
                {format(new Date(post.published_at), "MMMM dd, yyyy")}
              </time>
              <span className="text-sm text-muted-foreground flex items-center gap-1" itemProp="author" itemScope itemType="https://schema.org/Person">
                <User className="h-4 w-4" />
                <span itemProp="name">{post.author_name}</span>
              </span>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Eye className="h-4 w-4" />
                {post.view_count || 0} views
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-4" itemProp="headline">
              {post.title}
            </h1>

            {post.excerpt && (
              <p className="text-xl text-muted-foreground" itemProp="description">
                {post.excerpt}
              </p>
            )}
            
            {post.updated_at && (
              <meta itemProp="dateModified" content={post.updated_at} />
            )}
            <meta itemProp="url" content={`https://spotlightadz.ma/blog/${post.slug}`} />
            <span itemProp="publisher" itemScope itemType="https://schema.org/Organization" className="hidden">
              <meta itemProp="name" content="SpotlightAdz" />
              <span itemProp="logo" itemScope itemType="https://schema.org/ImageObject" className="hidden">
                <meta itemProp="url" content="https://spotlightadz.ma/logo.png" />
              </span>
            </span>
          </header>

          {/* Featured Image */}
          {post.featured_image && (
            <figure className="mb-8 rounded-lg overflow-hidden">
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-auto"
                loading="eager"
                itemProp="image"
              />
            </figure>
          )}

          {/* Content */}
          <div className="prose prose-lg dark:prose-invert max-w-none mb-8" itemProp="articleBody">
            <ReactMarkdown>{post.content}</ReactMarkdown>
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <footer className="flex flex-wrap gap-2 mb-8">
              {post.tags.map((tag: string, index: number) => (
                <Badge key={index} variant="outline" itemProp="keywords">
                  {tag}
                </Badge>
              ))}
            </footer>
          )}

          {/* CTA */}
          <aside className="border-t border-border pt-8">
            <div className="bg-primary/5 rounded-lg p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">
                Ready to Grow Your Business?
              </h3>
              <p className="text-muted-foreground mb-6">
                Let SpotlightAdz help you achieve your digital marketing goals. Visit{" "}
                <a href="https://spotlightadz.ma" className="text-primary hover:underline" rel="noopener">spotlightadz.ma</a> to get started.
              </p>
              <Button size="lg" onClick={() => navigate("/#request")}>
                Get Started Today
              </Button>
            </div>
          </aside>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
